/**
 * Session storage key and types for geo-gate debug logs.
 * Writes are gated by GEO_DEBUG_PANEL_ENABLED (config or cookie override).
 */

import { FeatureToggleService } from '../FeatureToggleService'

export const GEO_GATE_DEBUG_KEY = 'geo-gate-debug'

const GEO_GATE_LOG_STAGES = [
	'config',
	'start',
	'auth',
	'spoof',
	'spoof-check',
	'sdk-call',
	'locate',
	'phase-check',
	'outcome',
	'redirect',
	'guidance',
	'protection',
] as const

export type GeoGateLogStage = (typeof GEO_GATE_LOG_STAGES)[number]

export interface GeoGateLogEntry {
	timestamp: number
	stage: GeoGateLogStage
	message: string
}

export function isGeoGateLogEntry(value: unknown): value is GeoGateLogEntry {
	if (!value || typeof value !== 'object') return false
	const obj = value as Record<string, unknown>
	return (
		typeof obj.timestamp === 'number' &&
		typeof obj.message === 'string' &&
		typeof obj.stage === 'string' &&
		GEO_GATE_LOG_STAGES.some((valid) => valid === obj.stage)
	)
}

export function parseGeoGateLogs(raw: string | null): GeoGateLogEntry[] {
	if (!raw) return []
	try {
		const parsed = JSON.parse(raw)
		if (!Array.isArray(parsed)) return []
		return parsed.filter(isGeoGateLogEntry)
	} catch {
		return []
	}
}

export function formatLogTime(ts: number): string {
	const d = new Date(ts)
	return d.toTimeString().slice(0, 8)
}

/**
 * Appends a log entry to session storage. Used by geo-gate-redirect and other
 * geo-gate modules for session-based debugging (visible in geo-debug "Last run log").
 * No-op when geo debug panel is disabled (config and cookie).
 */
export function appendGeoGateLog(stage: GeoGateLogStage, message: string): void {
	if (!FeatureToggleService.geoDebugPanelFeature?.isEnabled()) return
	if (typeof sessionStorage === 'undefined') return
	try {
		const raw = sessionStorage.getItem(GEO_GATE_DEBUG_KEY)
		const logs: GeoGateLogEntry[] = raw ? parseGeoGateLogs(raw) : []
		logs.push({ timestamp: Date.now(), stage, message })
		sessionStorage.setItem(GEO_GATE_DEBUG_KEY, JSON.stringify(logs))
	} catch {
		// Ignore; session log is for debugging only
	}
}
